import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { ordersAPI } from '../lib/api';
import { formatCurrency, formatDate, getStatusLabel, getStatusColor, cn } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
    Search,
    Truck,
    Package,
    CheckCircle,
    XCircle,
    Phone,
    MapPin,
    Camera,
    Pen,
    RefreshCw,
    User,
    Banknote
} from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

// Delivery status options for driver
const deliveryStatusOptions = [
    { value: 'livre', label: 'Livré', icon: CheckCircle, color: 'text-green-600' },
    { value: 'client_absent', label: 'Client ne répond pas', icon: Phone, color: 'text-amber-600' },
    { value: 'client_refuse', label: 'Client refuse', icon: XCircle, color: 'text-red-600' },
];

export default function Livraisons() {
    const { user } = useAuth();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [deliveryDialogOpen, setDeliveryDialogOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [deliveryStatus, setDeliveryStatus] = useState('');
    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [signature, setSignature] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    
    // Signature canvas ref
    const canvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);

    const fetchOrders = useCallback(async () => {
        try {
            const params = { status: 'expedie' };
            if (search) params.search = search;
            
            const response = await ordersAPI.getAll(params);
            // Filter only orders assigned to this driver or for delivery by BBM
            const data = response.data.filter(o => 
                o.status === 'expedie' || 
                (o.assigned_driver_id === user?.id) ||
                (user?.role === 'admin')
            );
            setOrders(data);
        } catch (error) {
            console.error('Error fetching orders:', error);
            toast.error('Erreur lors du chargement des livraisons');
        } finally {
            setLoading(false);
        }
    }, [search, user]);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    // Handle photo upload
    const handlePhotoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setPhotoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPhotoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // Signature canvas handlers
    const startDrawing = (e) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        
        setIsDrawing(true);
        const ctx = canvas.getContext('2d');
        const rect = canvas.getBoundingClientRect();
        const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
        const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
        ctx.beginPath();
        ctx.moveTo(x, y);
    };

    const draw = (e) => {
        if (!isDrawing) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        
        const ctx = canvas.getContext('2d');
        const rect = canvas.getBoundingClientRect();
        const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
        const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
        ctx.lineTo(x, y);
        ctx.stroke();
    };

    const stopDrawing = () => {
        setIsDrawing(false);
        const canvas = canvasRef.current;
        if (canvas) {
            setSignature(canvas.toDataURL());
        }
    };

    const clearSignature = () => {
        const canvas = canvasRef.current;
        if (canvas) {
            const ctx = canvas.getContext('2d');
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.fillStyle = '#fff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            setSignature(null);
        }
    };

    // Initialize canvas
    useEffect(() => {
        if (deliveryDialogOpen && canvasRef.current) {
            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = '#fff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.strokeStyle = '#000';
            ctx.lineWidth = 2;
            ctx.lineCap = 'round';
        }
    }, [deliveryDialogOpen]);

    const openDeliveryDialog = (order) => {
        setSelectedOrder(order);
        setDeliveryStatus('');
        setPhotoFile(null);
        setPhotoPreview(null);
        setSignature(null);
        setDeliveryDialogOpen(true);
    };

    const handleDeliverySubmit = async () => {
        if (!deliveryStatus) {
            toast.error('Veuillez sélectionner un statut de livraison');
            return;
        }

        if (deliveryStatus === 'livre' && !photoPreview) {
            toast.error('Veuillez prendre une photo de l\'argent reçu');
            return;
        }

        setSubmitting(true);
        try {
            // Update order with delivery info
            const updateData = {
                delivery_status: deliveryStatus,
                status: deliveryStatus === 'livre' ? 'livre' : 
                        deliveryStatus === 'client_refuse' ? 'retourne' : 'expedie',
            };

            // Add photo and signature as base64
            if (photoPreview) {
                updateData.delivery_photo_cash = photoPreview;
            }
            if (signature) {
                updateData.delivery_signature = signature;
            }

            await ordersAPI.update(selectedOrder.id, updateData);
            
            toast.success(
                deliveryStatus === 'livre' ? 'Livraison confirmée!' :
                deliveryStatus === 'client_refuse' ? 'Commande marquée comme retournée' :
                'Statut mis à jour'
            );
            
            setDeliveryDialogOpen(false);
            fetchOrders();
        } catch (error) {
            const message = error.response?.data?.detail || 'Erreur lors de la mise à jour';
            toast.error(message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Layout>
            <div className="space-y-6" data-testid="livraisons-page">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-3">
                            <Truck className="w-8 h-8 text-primary" />
                            Mes Livraisons
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Gérez vos livraisons en cours
                        </p>
                    </div>
                    <Button
                        variant="outline"
                        onClick={fetchOrders}
                        disabled={loading}
                    >
                        <RefreshCw className={cn('w-4 h-4 mr-2', loading && 'animate-spin')} />
                        Actualiser
                    </Button>
                </div>

                {/* Search */}
                <Card className="card-luxury">
                    <CardContent className="p-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder="Rechercher par N°, client, téléphone..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-10 input-luxury"
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card className="bg-purple-50 border-purple-200">
                        <CardContent className="p-4 flex items-center gap-4">
                            <div className="p-3 bg-purple-100 rounded-lg">
                                <Truck className="w-6 h-6 text-purple-600" />
                            </div>
                            <div>
                                <p className="text-sm text-purple-600">À livrer</p>
                                <p className="text-2xl font-bold text-purple-700">{orders.length}</p>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Orders List */}
                <Card className="card-luxury">
                    <CardHeader>
                        <CardTitle>Commandes à livrer</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        {loading ? (
                            <div className="flex justify-center py-12">
                                <div className="spinner" />
                            </div>
                        ) : orders.length === 0 ? (
                            <div className="text-center py-12 text-muted-foreground">
                                <Truck className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                <p>Aucune livraison en attente</p>
                            </div>
                        ) : (
                            <div className="divide-y">
                                {orders.map((order) => (
                                    <div 
                                        key={order.id} 
                                        className="p-4 hover:bg-muted/50 transition-colors"
                                    >
                                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                            <div className="space-y-2">
                                                <div className="flex items-center gap-3">
                                                    <span className="font-mono font-bold text-lg">
                                                        {order.n_commande}
                                                    </span>
                                                    <Badge className={cn(getStatusColor(order.status))}>
                                                        {getStatusLabel(order.status)}
                                                    </Badge>
                                                </div>
                                                
                                                <div className="flex items-center gap-2 text-sm">
                                                    <User className="w-4 h-4 text-muted-foreground" />
                                                    <span className="font-medium">{order.full_name}</span>
                                                </div>
                                                
                                                <div className="flex items-center gap-2 text-sm">
                                                    <Phone className="w-4 h-4 text-muted-foreground" />
                                                    <a href={`tel:${order.phone}`} className="text-primary hover:underline">
                                                        {order.phone}
                                                    </a>
                                                </div>
                                                
                                                <div className="flex items-start gap-2 text-sm">
                                                    <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
                                                    <span className="text-muted-foreground">{order.adresse}</span>
                                                </div>
                                                
                                                <div className="flex items-center gap-2 text-sm">
                                                    <Package className="w-4 h-4 text-muted-foreground" />
                                                    <span>{order.produit}</span>
                                                </div>
                                            </div>
                                            
                                            <div className="flex flex-col items-end gap-3">
                                                <div className="text-right">
                                                    <p className="text-xs text-muted-foreground">Montant à collecter</p>
                                                    <p className="text-2xl font-bold text-primary">
                                                        {formatCurrency(order.prix_total)}
                                                    </p>
                                                </div>
                                                
                                                <Button 
                                                    className="btn-luxury"
                                                    onClick={() => openDeliveryDialog(order)}
                                                >
                                                    <CheckCircle className="w-4 h-4 mr-2" />
                                                    Confirmer Livraison
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Delivery Confirmation Dialog */}
                <Dialog open={deliveryDialogOpen} onOpenChange={setDeliveryDialogOpen}>
                    <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <Truck className="w-5 h-5" />
                                Confirmer la livraison
                            </DialogTitle>
                        </DialogHeader>
                        
                        {selectedOrder && (
                            <div className="space-y-6">
                                {/* Order Summary */}
                                <div className="p-4 bg-muted/30 rounded-lg space-y-2">
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">Commande</span>
                                        <span className="font-mono font-bold">{selectedOrder.n_commande}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">Client</span>
                                        <span className="font-medium">{selectedOrder.full_name}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">Montant</span>
                                        <span className="font-bold text-lg text-primary">
                                            {formatCurrency(selectedOrder.prix_total)}
                                        </span>
                                    </div>
                                </div>

                                {/* Delivery Status */}
                                <div className="space-y-3">
                                    <Label className="text-base font-semibold">Statut de livraison</Label>
                                    <div className="grid grid-cols-1 gap-2">
                                        {deliveryStatusOptions.map((option) => {
                                            const Icon = option.icon;
                                            return (
                                                <button
                                                    key={option.value}
                                                    type="button"
                                                    onClick={() => setDeliveryStatus(option.value)}
                                                    className={cn(
                                                        "flex items-center gap-3 p-4 rounded-lg border-2 transition-all text-left",
                                                        deliveryStatus === option.value
                                                            ? "border-primary bg-primary/5"
                                                            : "border-muted hover:border-primary/50"
                                                    )}
                                                >
                                                    <Icon className={cn("w-6 h-6", option.color)} />
                                                    <span className="font-medium">{option.label}</span>
                                                    {deliveryStatus === option.value && (
                                                        <CheckCircle className="w-5 h-5 text-primary ml-auto" />
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Photo Upload - Only for successful delivery */}
                                {deliveryStatus === 'livre' && (
                                    <div className="space-y-3">
                                        <Label className="text-base font-semibold flex items-center gap-2">
                                            <Camera className="w-4 h-4" />
                                            Photo de l'argent reçu *
                                        </Label>
                                        
                                        {photoPreview ? (
                                            <div className="relative">
                                                <img 
                                                    src={photoPreview} 
                                                    alt="Preview" 
                                                    className="w-full h-48 object-cover rounded-lg border"
                                                />
                                                <Button
                                                    type="button"
                                                    variant="destructive"
                                                    size="sm"
                                                    className="absolute top-2 right-2"
                                                    onClick={() => {
                                                        setPhotoFile(null);
                                                        setPhotoPreview(null);
                                                    }}
                                                >
                                                    <XCircle className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        ) : (
                                            <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                                                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                                    <Banknote className="w-10 h-10 mb-3 text-muted-foreground" />
                                                    <p className="mb-2 text-sm text-muted-foreground">
                                                        <span className="font-semibold">Cliquez</span> pour prendre une photo
                                                    </p>
                                                    <p className="text-xs text-muted-foreground">
                                                        Photo de l'argent reçu
                                                    </p>
                                                </div>
                                                <input 
                                                    type="file" 
                                                    className="hidden" 
                                                    accept="image/*"
                                                    capture="environment"
                                                    onChange={handlePhotoChange}
                                                />
                                            </label>
                                        )}
                                    </div>
                                )}

                                {/* Signature - Only for successful delivery */}
                                {deliveryStatus === 'livre' && (
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between">
                                            <Label className="text-base font-semibold flex items-center gap-2">
                                                <Pen className="w-4 h-4" />
                                                Signature du client
                                            </Label>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={clearSignature}
                                            >
                                                Effacer
                                            </Button>
                                        </div>
                                        
                                        <div className="border-2 rounded-lg overflow-hidden">
                                            <canvas
                                                ref={canvasRef}
                                                width={400}
                                                height={150}
                                                className="w-full touch-none cursor-crosshair bg-white"
                                                onMouseDown={startDrawing}
                                                onMouseMove={draw}
                                                onMouseUp={stopDrawing}
                                                onMouseLeave={stopDrawing}
                                                onTouchStart={startDrawing}
                                                onTouchMove={draw}
                                                onTouchEnd={stopDrawing}
                                            />
                                        </div>
                                        <p className="text-xs text-muted-foreground text-center">
                                            Demandez au client de signer ci-dessus
                                        </p>
                                    </div>
                                )}

                                <DialogFooter className="gap-2">
                                    <Button 
                                        type="button" 
                                        variant="outline" 
                                        onClick={() => setDeliveryDialogOpen(false)}
                                    >
                                        Annuler
                                    </Button>
                                    <Button 
                                        className="btn-luxury"
                                        onClick={handleDeliverySubmit}
                                        disabled={submitting || !deliveryStatus}
                                    >
                                        {submitting ? 'Enregistrement...' : 'Confirmer'}
                                    </Button>
                                </DialogFooter>
                            </div>
                        )}
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
}
